import * as Dialog from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import { podcastServices } from '../services/podcastApi';
import toast from 'react-hot-toast';
import { setPodcastUrl } from 'store/slices/podcastPlayback';
import { LoaderIcon } from 'lucide-react';
// import toast from 'react-hot-toast';

const GeneratePodcast = () => {
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { prompt, duration } = useSelector(
    (state: RootState) => state.podcastGen
  );
  const { dialogues } = useSelector(
    (state: RootState) => state.podcastRes.data
  );
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);

  const savePodcastStateToLocalStorage = () => {
    const podcastState = {
      prompt,
      duration,
      dialogues: dialogues.map((dialogue) => ({
        character_name: dialogue.character_name,
        voice_name: dialogue.voice_name,
        dialogue: dialogue.dialogue,
        role: dialogue.role,
        preview_url: dialogue.preview_url,
        presigned_preview_url: dialogue.presigned_preview_url,
      })),
    };
    localStorage.setItem('pending_podcast', JSON.stringify(podcastState));
  };

  const handleGenerate = async () => {
    // Check if user is logged in
    if (!isAuthenticated) {
      // Save the current podcast state to localStorage before showing login message
      savePodcastStateToLocalStorage();
      
      toast.error(
        <div className="flex flex-col">
          <span>Please log in or sign up to generate podcast</span>
          <button 
            className="text-sm text-blue-500 hover:text-blue-700 underline mt-1 self-start"
            onClick={() => navigate('/auth/login')}
          >
            Go to login
          </button>
        </div>,
        { duration: 5000 }
      );
      
      setOpen(false);
      return;
    }
    
    setLoading(true);
    try {
      const generatePodcastInput = {
        title: 'Podcast', // Constant title
        prompt,
        duration,
        script: dialogues.map((dialogue) => ({
          character_name: dialogue.character_name,
          voice_name: dialogue.voice_name,
          dialogue: dialogue.dialogue,
          role: dialogue.role,
          preview_url: dialogue.preview_url,
        })),
      };

      // Call API
      const response =
        await podcastServices.generatePodcast(generatePodcastInput);

      if (response.success) {
        // Navigate to the generated page
        dispatch(setPodcastUrl(response.data.podcast_url));
        navigate('/ai-podcast-generator/generated');
        toast.success('Podcast generated successfully!');
        
        // Clear the saved podcast state as it's been successfully generated
        localStorage.removeItem('pending_podcast');
      } else {
        toast.error(response.message);
        throw new Error(response.message);
      }
    } catch (error) {
      toast.error('Failed to generate podcast. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <Button
            size="sm"
            variant="customPrimary"
            className="text-sm font-normal px-4 w-full"
          >
            Generate Podcast
          </Button>
        </Dialog.Trigger>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-20" />
        <Dialog.Content className="fixed inset-0 flex items-center justify-center p-4 text-text-primary z-50">
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-[90%] sm:max-w-[480px] flex flex-col gap-1">
            <Dialog.Title className="text-2xl text-left font-bold ">
              Generate Podcast
            </Dialog.Title>
            <Dialog.Description className=" mt-2 text-left text-[15px] font-normal">
              Are you ready to generate your podcast? You won&apos;t be able to
              make edits once it&apos;s generated.
            </Dialog.Description>
            <div className="flex flex-row justify-end mt-6 w-full gap-2">
              <Button
                variant="customSecondary"
                size="sm"
                onClick={() => setOpen(false)}
                className="text-[14px]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                variant="customPrimary"
                size="sm"
                className="text-[14px]"
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin">
                    <LoaderIcon size={20} />
                  </div>
                )}
                Generate
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

export default GeneratePodcast;
